import ContentLayout from '@/components/layout/ContentLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ROUTE } from '@/routes/constants';
import { getAdminAllLeaderboards, getStudentMonthlyAchievementAudit, searchStudents } from '@/services/generatedAPIs';
import { AdminAllLeaderboardsResponse, MonthlyAchievementAuditResponse, SchoolLeaderboardEntry } from '@/services/generatedAPIs.schemas';
import { AlertTriangle, FileSearch } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

const formatMetadata = (raw?: string) => {
  if (!raw) return null;
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const GamificationLeaderboardsPage = () => {
  const navigate = useNavigate();
  const [month, setMonth] = useState(getCurrentMonth());
  const [data, setData] = useState<AdminAllLeaderboardsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [auditTarget, setAuditTarget] = useState<{ studentId: number; studentName: string; schoolName?: string } | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditData, setAuditData] = useState<MonthlyAchievementAuditResponse | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);

  const fetchLeaderboards = async (targetMonth: string) => {
    setLoading(true);
    try {
      const response = await getAdminAllLeaderboards({ month: targetMonth });
      if (response.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (response.code === 200 && response.data) {
        setData(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch leaderboards');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error fetching leaderboards', error);
      toast.error('Failed to fetch leaderboards');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaderboards(month);
  }, []);

  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth);
    fetchLeaderboards(newMonth);
  };

  const resolveStudentName = (studentId?: number) => {
    if (!studentId || !data?.studentNames) return String(studentId ?? '-');
    return data.studentNames[String(studentId)] ?? String(studentId);
  };

  const openAudit = async (studentId?: number, schoolName?: string) => {
    if (!studentId) return;
    const studentName = resolveStudentName(studentId);
    setAuditTarget({ studentId, studentName, schoolName });
    setAuditData(null);
    setAuditError(null);
    setAuditLoading(true);
    try {
      const searchResp = await searchStudents({
        name: studentName,
        schoolName: schoolName || undefined,
        page: 1,
        size: 50,
      });
      if (searchResp.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      const match = searchResp.data?.students?.find((s) => s.studentId === studentId);
      const email = match?.email;
      if (!email) {
        setAuditError('Could not resolve student email — audit unavailable.');
        return;
      }

      const auditResp = await getStudentMonthlyAchievementAudit({ email, month });
      if (auditResp.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      if (auditResp.code === 200 && auditResp.data) {
        setAuditData(auditResp.data);
      } else {
        setAuditError(auditResp.message || 'Failed to fetch audit');
      }
    } catch (error: any) {
      if (error?.response?.data?.code === 1003 || error?.code === 1003) {
        navigate(ROUTE.TEACHER.HOMEWORK_OVERVIEW, { replace: true });
        return;
      }
      console.error('Error fetching monthly achievement audit', error);
      setAuditError('Failed to fetch audit');
    } finally {
      setAuditLoading(false);
    }
  };

  const renderLeaderboardTable = (entries: SchoolLeaderboardEntry[], type: string) => {
    if (!entries || entries.length === 0) {
      return <p className="text-sm text-muted-foreground py-2">No entries for {type}</p>;
    }
    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>School Rank</TableHead>
            <TableHead>Student</TableHead>
            <TableHead>Total KP</TableHead>
            <TableHead>Total GS</TableHead>
            <TableHead>Total Points</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => (
            <TableRow key={entry.studentId}>
              <TableCell>{entry.rankInSchool ?? '-'}</TableCell>
              <TableCell>{resolveStudentName(entry.studentId)}</TableCell>
              <TableCell>{entry.totalKp ?? 0}</TableCell>
              <TableCell>{entry.totalGs ?? 0}</TableCell>
              <TableCell>{entry.totalPoints ?? 0}</TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="icon"
                  title="View monthly achievement audit"
                  onClick={() => openAudit(entry.studentId, entry.schoolName)}
                >
                  <FileSearch className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  return (
    <ContentLayout title="Leaderboards Monitor">
      {/* Month Selector */}
      <div className="flex items-end gap-3 m-4">
        <div className="grid gap-1.5">
          <Label htmlFor="month">Month</Label>
          <Input
            id="month"
            type="month"
            value={month}
            onChange={(e) => handleMonthChange(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8 text-muted-foreground">Loading...</div>
      ) : data ? (
        <div className="px-4 space-y-6">
          <p className="text-sm text-muted-foreground">Season: {data.seasonMonth ?? month}</p>

          {/* Hall of Fame */}
          <div className="bg-muted rounded-2xl p-4">
            <h3 className="text-lg font-semibold mb-3">Hall of Fame</h3>
            {data.hallOfFame && data.hallOfFame.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Global Rank</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Total KP</TableHead>
                    <TableHead>Total GS</TableHead>
                    <TableHead>Total Points</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.hallOfFame.map((entry) => (
                    <TableRow key={entry.studentId}>
                      <TableCell>{entry.rankGlobal ?? '-'}</TableCell>
                      <TableCell>{resolveStudentName(entry.studentId)}</TableCell>
                      <TableCell>{entry.schoolName ?? '-'}</TableCell>
                      <TableCell>{entry.totalKp ?? 0}</TableCell>
                      <TableCell>{entry.totalGs ?? 0}</TableCell>
                      <TableCell>{entry.totalPoints ?? 0}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="View monthly achievement audit"
                          onClick={() => openAudit(entry.studentId, entry.schoolName)}
                        >
                          <FileSearch className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-sm text-muted-foreground">No Hall of Fame entries</p>
            )}
          </div>

          {/* Per-School Leaderboards */}
          {data.schoolLeaderboards && Object.entries(data.schoolLeaderboards).map(([schoolKey, school]) => (
            <div key={schoolKey} className="bg-muted rounded-2xl p-4">
              <h3 className="text-lg font-semibold mb-3">
                {school.schoolName ?? schoolKey}
                {data.schoolNamesCn?.[schoolKey] && (
                  <span className="text-sm font-normal text-muted-foreground ml-2">
                    {data.schoolNamesCn[schoolKey]}
                  </span>
                )}
              </h3>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Master</h4>
                  {renderLeaderboardTable(school.master ?? [], 'Master')}
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Grit</h4>
                  {renderLeaderboardTable(school.grit ?? [], 'Grit')}
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Rocket</h4>
                  {renderLeaderboardTable(school.rocket ?? [], 'Rocket')}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex justify-center p-8 text-muted-foreground">No data available</div>
      )}

      {/* Monthly Achievement Audit Dialog */}
      <Dialog
        open={!!auditTarget}
        onOpenChange={(open) => {
          if (!open) {
            setAuditTarget(null);
            setAuditData(null);
            setAuditError(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Monthly Achievement Audit</DialogTitle>
            {auditTarget && (
              <p className="text-xs text-muted-foreground">
                {auditData?.studentName ?? auditTarget.studentName}
                {auditData?.studentEmail && <> · {auditData.studentEmail}</>}
                {' · '}{auditData?.schoolName ?? auditTarget.schoolName ?? '-'}
                {' · '}{auditData?.month ?? month}
              </p>
            )}
          </DialogHeader>
          {auditLoading ? (
            <div className="flex justify-center p-6 text-muted-foreground text-sm">Loading...</div>
          ) : auditError ? (
            <p className="text-sm text-destructive">{auditError}</p>
          ) : auditData ? (
            <div className="space-y-3">
              {/* Counts */}
              <div className="grid grid-cols-4 gap-2 text-center">
                {[
                  { label: 'Assigned', value: auditData.assignedCount },
                  { label: 'Completed', value: auditData.completedCount },
                  { label: 'On-Time', value: auditData.onTimeCount },
                  { label: 'Perfect', value: auditData.perfectCount },
                ].map((c) => (
                  <div key={c.label} className="bg-muted rounded-lg py-2">
                    <p className="text-[11px] text-muted-foreground">{c.label}</p>
                    <p className="text-lg font-semibold leading-tight">{c.value ?? 0}</p>
                  </div>
                ))}
              </div>

              {/* On-Time + Perfect blocks */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {([
                  { label: 'On-Time Bonus', eligible: auditData.onTimeEligible, discrepancy: auditData.onTimeDiscrepancy, bonus: auditData.onTimeBonus },
                  { label: 'Perfect Bonus', eligible: auditData.perfectEligible, discrepancy: auditData.perfectDiscrepancy, bonus: auditData.perfectBonus },
                ] as const).map((block) => {
                  const metadataText = formatMetadata(block.bonus?.metadata);
                  return (
                    <div key={block.label} className="bg-muted rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-sm font-medium">{block.label}</p>
                        <div className="flex items-center gap-1">
                          <Badge variant={block.eligible ? 'default' : 'secondary'}>
                            {block.eligible ? 'Eligible' : 'Not eligible'}
                          </Badge>
                          {block.discrepancy && (
                            <Badge variant="destructive" className="gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              Discrepancy
                            </Badge>
                          )}
                        </div>
                      </div>
                      {block.bonus ? (
                        <div className="text-xs space-y-0.5">
                          <p>
                            <span className="text-muted-foreground">Status:</span> {block.bonus.status ?? '-'}
                            {' · '}
                            <span className="text-muted-foreground">Amount:</span> {block.bonus.pointsAmount ?? 0} {block.bonus.currencyType ?? ''}
                          </p>
                          <p>
                            <span className="text-muted-foreground">Awarded:</span> {block.bonus.awardedAt ?? '-'}
                            {block.bonus.pointId != null && <> · <span className="text-muted-foreground">ID:</span> {block.bonus.pointId}</>}
                          </p>
                          {metadataText && (
                            <details>
                              <summary className="cursor-pointer text-muted-foreground">Metadata</summary>
                              <pre className="bg-background rounded p-2 mt-1 whitespace-pre-wrap break-words max-h-32 overflow-auto">
                                {metadataText}
                              </pre>
                            </details>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground">No bonus awarded.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setAuditTarget(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ContentLayout>
  );
};

export default GamificationLeaderboardsPage;
